import { ShareEmailOptions } from '@/Enums/Forecaster.enum';
import { ISharingUser } from '@/Interfaces/SharingUser.interface';
import ModalBody from '@/components/Modal/common/ModalBody';
import { SHARE_EMAIL_OPTIONS } from '@/constants/forecaster';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSharesUsersData, selectSharesUsersLoading } from '@/store/selectors/sharesUsers.selectors';
import { clearSharesUsers, getSharesUsers } from '@/store/slices/sharesUsers.slice';
import { SharesTargets } from '@/types/shares.types';
import { Autocomplete, Button, FormHelperText, Modal, TextField } from '@mui/material';
import debounce from 'debounce';
import { useCallback, useEffect, useState } from 'react';
import RadioSwitch from '../RadioSwitch';

interface IProps {
  open: boolean;
  targetTable: SharesTargets;
  targetId: number;
  title?: string;
  shareWithOrgHelperText?: string;
  onModalClose: () => void;
  onSubmit: (data: { receivingUserIds: number[]; shareWithOrg: boolean; targetId: number }) => void;
}

const ShareModal = ({
  open,
  targetTable,
  targetId,
  title = 'Share Forecast',
  shareWithOrgHelperText = '*This forecast will be shared with all of your team members',
  onModalClose,
  onSubmit,
}: IProps) => {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');
  const [shareMethod, setShareMethod] = useState<ShareEmailOptions>(ShareEmailOptions.WHOLE_ORG);

  const handleChange = useCallback(
    (value: string) => {
      setShareMethod(value as ShareEmailOptions);
    },
    [setShareMethod],
  );

  const [searchResultIds, setSearchResultIds] = useState<number[]>([]);

  const shareUserList = useAppSelector(selectSharesUsersData);
  const shareUserListLoading = useAppSelector(selectSharesUsersLoading);

  const handleSearch = useCallback(
    debounce(() => {
      if (targetId === null) {
        return;
      }
      dispatch(
        getSharesUsers({
          targetId,
          targetTable,
          query,
        }),
      );
    }, 500),
    [dispatch, getSharesUsers, query],
  );

  const onResultChange = useCallback(
    (result: ISharingUser[]) => {
      const ids = result.map(item => item.id);
      setSearchResultIds(ids);
    },
    [setSearchResultIds],
  );

  const submitResults = useCallback(() => {
    onSubmit({
      receivingUserIds: searchResultIds,
      shareWithOrg: shareMethod === ShareEmailOptions.WHOLE_ORG,
      targetId,
    });
    dispatch(clearSharesUsers());
  }, [dispatch, searchResultIds, targetId, shareMethod, onSubmit]);

  const onClose = useCallback(() => {
    setShareMethod(ShareEmailOptions.WHOLE_ORG);
    dispatch(clearSharesUsers());
    onModalClose();
  }, [dispatch, onModalClose]);

  useEffect(() => {
    if (query.length > 2) {
      handleSearch();
    }
  }, [query]);

  useEffect(() => {
    return () => {
      dispatch(clearSharesUsers());
    };
  }, [dispatch]);

  return (
    <Modal open={open}>
      <ModalBody>
        <h1 className='font-semibold'>{title}</h1>
        <div className='flex items-center space-x-4 my-2'>
          <div className='flex mb-2'>
            <RadioSwitch value={shareMethod} onChange={handleChange} options={SHARE_EMAIL_OPTIONS} />
          </div>
        </div>
        {shareMethod === ShareEmailOptions.WHOLE_ORG ? (
          <FormHelperText>{shareWithOrgHelperText}</FormHelperText>
        ) : (
          <Autocomplete
            multiple
            onInputChange={(_, value) => setQuery(value)}
            onChange={(_, res) => onResultChange(res)}
            renderInput={params => <TextField {...params} label='Enter emails' />}
            options={shareUserList}
            loading={shareUserListLoading}
            getOptionLabel={option => option.email}
          />
        )}

        <div className='mt-5 flex justify-end gap-[12px]'>
          <Button color='inherit' variant='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='contained'
            type='submit'
            disabled={!searchResultIds.length && shareMethod === ShareEmailOptions.BY_EMAIL}
            onClick={submitResults}
          >
            Confirm
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ShareModal;
